/**
 * Admin API client for the Keystone backend.
 *
 * The admin panel talks to the same-origin FastAPI backend via /api/*.
 * Auth is a simple bearer JWT issued by /api/admin/login; the token lives
 * in sessionStorage and is dropped on sign-out or tab close.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'keystone.admin.jwt';

export function getToken() {
  try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
export function setToken(t) {
  try { sessionStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
}
export function clearToken() {
  try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

async function request(path, init = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(init.headers || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    throw new Error('Session expired. Sign in again.');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let msg = `${res.status} ${res.statusText}`;
    try {
      const p = JSON.parse(text);
      if (p?.detail) msg = typeof p.detail === 'string' ? p.detail : JSON.stringify(p.detail);
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  return res.json();
}

export async function login(password) {
  const res = await fetch(`${API}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('Incorrect password.');
    throw new Error(`Login failed (${res.status}).`);
  }
  const data = await res.json();
  setToken(data.token);
  return data;
}

export const listDrafts = () => request('/api/admin/articles/drafts');
export const listPublished = () => request('/api/articles');
export const startGeneration = () =>
  request('/api/admin/articles/generate', { method: 'POST' });
export const getJob = (jobId) =>
  request(`/api/admin/articles/jobs/${encodeURIComponent(jobId)}`);
export const approveDraft = (id) =>
  request(`/api/admin/articles/${id}/approve`, { method: 'POST' });
export const discardDraft = (id) =>
  request(`/api/admin/articles/${id}`, { method: 'DELETE' });
export const patchArticle = (id, patch) =>
  request(`/api/admin/articles/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
